import React, { useEffect, useState } from 'react';
import { useOrder, OrderStatus, OnlineOrder } from '../contexts/OrderContext';
import './OrderTracking.css';

interface OrderTrackingProps {
  orderId?: string;
  onClose?: () => void;
  showHistory?: boolean;
}

const statusSteps: { status: OrderStatus; label: string; icon: string }[] = [
  { status: 'confirmed', label: 'Confirmed', icon: 'check' },
  { status: 'preparing', label: 'Preparing', icon: 'chef' },
  { status: 'ready', label: 'Ready', icon: 'bell' },
  { status: 'completed', label: 'Complete', icon: 'star' }
];

const deliverySteps: { status: OrderStatus; label: string; icon: string }[] = [
  { status: 'confirmed', label: 'Confirmed', icon: 'check' },
  { status: 'preparing', label: 'Preparing', icon: 'chef' },
  { status: 'ready', label: 'Ready', icon: 'package' },
  { status: 'out_for_delivery', label: 'On the Way', icon: 'truck' },
  { status: 'delivered', label: 'Delivered', icon: 'home' }
];

const OrderTracking: React.FC<OrderTrackingProps> = ({ orderId, onClose, showHistory = false }) => {
  const {
    currentOrder,
    orderHistory,
    favorites,
    addToFavorites,
    isConnected,
    enableNotifications,
    setEnableNotifications
  } = useOrder();

  const [activeTab, setActiveTab] = useState<'tracking' | 'history' | 'favorites'>('tracking');
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [favoriteName, setFavoriteName] = useState('');

  const order = currentOrder;
  const steps = order?.orderType === 'delivery' ? deliverySteps : statusSteps;

  const getCurrentStepIndex = () => {
    if (!order) return -1;
    const index = steps.findIndex(s => s.status === order.status);
    if (index >= 0) return index;
    // Handle pending state
    if (order.status === 'pending') return -1;
    // Handle completed states
    if (['completed', 'picked_up', 'delivered'].includes(order.status)) return steps.length - 1;
    return -1;
  };

  const currentStepIndex = getCurrentStepIndex();

  const getStatusMessage = () => {
    if (!order) return '';

    const messages: Record<OrderStatus, string> = {
      pending: 'Your order is being processed...',
      confirmed: 'Your order has been confirmed!',
      preparing: 'The kitchen is preparing your order',
      ready: order.orderType === 'pickup'
        ? 'Your order is ready for pickup!'
        : order.orderType === 'delivery'
          ? 'Your order is ready and waiting for driver'
          : 'Your order is ready!',
      out_for_delivery: 'Your order is on the way!',
      delivered: 'Your order has been delivered!',
      picked_up: 'Thank you for picking up your order!',
      completed: 'Order complete. Thank you!',
      cancelled: 'This order has been cancelled'
    };

    return messages[order.status] || '';
  };

  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleSaveToFavorites = () => {
    if (order && favoriteName.trim()) {
      addToFavorites(order, favoriteName.trim());
      setShowFavoriteModal(false);
      setFavoriteName('');
    }
  };

  const renderIcon = (icon: string, isActive: boolean, isCompleted: boolean) => {
    const color = isCompleted ? '#10b981' : isActive ? '#6366f1' : '#94a3b8';

    switch (icon) {
      case 'check':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        );
      case 'chef':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z" />
            <line x1="6" y1="17" x2="18" y2="17" />
          </svg>
        );
      case 'bell':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        );
      case 'package':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <path d="m16.5 9.4-9-5.19M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
          </svg>
        );
      case 'truck':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <rect x="1" y="3" width="15" height="13" />
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
            <circle cx="5.5" cy="18.5" r="2.5" />
            <circle cx="18.5" cy="18.5" r="2.5" />
          </svg>
        );
      case 'home':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        );
      case 'star':
        return (
          <svg viewBox="0 0 24 24" fill={isCompleted ? color : 'none'} stroke={color} strokeWidth="2">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        );
      default:
        return null;
    }
  };

  // Render tracking content
  const renderTrackingContent = () => {
    if (!order) {
      return (
        <div className="no-order">
          <p>No active order to track</p>
          <p className="hint">Place an order to see tracking here</p>
        </div>
      );
    }

    return (
      <>
        {/* Order Header */}
        <div className="tracking-header">
          <div className="order-info">
            <h2>Order #{order.orderId.slice(-8).toUpperCase()}</h2>
            <p className="store-name">{order.storeName}</p>
            <p className="order-type">{order.orderType.charAt(0).toUpperCase() + order.orderType.slice(1)}</p>
          </div>
          <div className="connection-status">
            <span className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`} />
            <span>{isConnected ? 'Live' : 'Polling'}</span>
          </div>
        </div>

        {/* Status Message */}
        <div className={`status-message status-${order.status}`}>
          <p>{getStatusMessage()}</p>
          {order.estimatedReadyTime && order.status !== 'completed' && (
            <p className="eta">Estimated: {formatTime(order.estimatedReadyTime)}</p>
          )}
        </div>

        {/* Progress Steps */}
        <div className="progress-tracker">
          {steps.map((step, index) => {
            const isCompleted = index < currentStepIndex;
            const isActive = index === currentStepIndex;

            return (
              <div
                key={step.status}
                className={`progress-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}
              >
                <div className="step-icon">
                  {renderIcon(step.icon, isActive, isCompleted)}
                </div>
                <span className="step-label">{step.label}</span>
                {index < steps.length - 1 && <div className="step-connector" />}
              </div>
            );
          })}
        </div>

        {/* Driver Info (for delivery) */}
        {order.orderType === 'delivery' && order.driver && order.status === 'out_for_delivery' && (
          <div className="driver-info">
            <h3>Your Driver</h3>
            <div className="driver-details">
              <div className="driver-avatar">
                {order.driver.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="driver-name">{order.driver.name}</p>
                {order.driver.vehicleInfo && (
                  <p className="vehicle-info">{order.driver.vehicleInfo}</p>
                )}
              </div>
              <a href={`tel:${order.driver.phone}`} className="call-driver">
                Call
              </a>
            </div>
            {order.driver.estimatedArrival && (
              <p className="eta">Arriving at {formatTime(order.driver.estimatedArrival)}</p>
            )}
          </div>
        )}

        {/* Order Items */}
        <div className="order-items-summary">
          <h3>Order Items</h3>
          {order.items.map((item, index) => (
            <div key={index} className="summary-item">
              <span>{item.quantity}x {item.name}</span>
              <span>${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className="summary-totals">
            <div className="summary-row">
              <span>Subtotal</span>
              <span>${order.subtotal.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Tax</span>
              <span>${order.tax.toFixed(2)}</span>
            </div>
            {order.tip > 0 && (
              <div className="summary-row">
                <span>Tip</span>
                <span>${order.tip.toFixed(2)}</span>
              </div>
            )}
            <div className="summary-row total">
              <span>Total</span>
              <span>${order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Status History */}
        <div className="status-history">
          <h3>Status Updates</h3>
          {order.statusHistory.map((entry, index) => (
            <div key={index} className="history-entry">
              <span className="history-time">{formatTime(entry.timestamp)}</span>
              <span className="history-status">{entry.status.replace(/_/g, ' ')}</span>
              {entry.note && <span className="history-note">{entry.note}</span>}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="tracking-actions">
          <button
            className="save-favorite-btn"
            onClick={() => setShowFavoriteModal(true)}
          >
            Save as Favorite
          </button>
        </div>
      </>
    );
  };

  // Render order history
  const renderHistoryContent = () => (
    <div className="order-history">
      <h2>Order History</h2>
      {orderHistory.length === 0 ? (
        <p className="empty-state">No previous orders</p>
      ) : (
        orderHistory.map(historyOrder => (
          <div key={historyOrder.orderId} className="history-order-card">
            <div className="history-order-header">
              <span className="history-order-id">#{historyOrder.orderId.slice(-8).toUpperCase()}</span>
              <span className="history-order-date">{formatDate(historyOrder.createdAt)}</span>
            </div>
            <p className="history-store">{historyOrder.storeName}</p>
            <p className="history-items">
              {historyOrder.items.map(i => `${i.quantity}x ${i.name}`).join(', ')}
            </p>
            <div className="history-order-footer">
              <span className="history-total">${historyOrder.total.toFixed(2)}</span>
              <span className={`history-status status-${historyOrder.status}`}>
                {historyOrder.status.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );

  // Render favorites
  const renderFavoritesContent = () => (
    <div className="favorites-list">
      <h2>Saved Favorites</h2>
      {favorites.length === 0 ? (
        <p className="empty-state">No saved favorites yet</p>
      ) : (
        favorites.map(fav => (
          <div key={fav.favoriteId} className="favorite-card">
            <div className="favorite-header">
              <h3>{fav.name}</h3>
              <span className="order-count">Ordered {fav.orderCount}x</span>
            </div>
            <p className="favorite-store">{fav.storeName}</p>
            <p className="favorite-items">
              {fav.items.map(i => `${i.quantity}x ${i.name}`).join(', ')}
            </p>
            <button className="reorder-btn">
              Reorder
            </button>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="order-tracking">
      {onClose && (
        <button className="close-tracking" onClick={onClose}>&times;</button>
      )}

      {/* Tab Navigation */}
      <div className="tracking-tabs">
        <button
          className={`tab ${activeTab === 'tracking' ? 'active' : ''}`}
          onClick={() => setActiveTab('tracking')}
        >
          Track Order
        </button>
        <button
          className={`tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
        <button
          className={`tab ${activeTab === 'favorites' ? 'active' : ''}`}
          onClick={() => setActiveTab('favorites')}
        >
          Favorites
        </button>
      </div>

      {/* Tab Content */}
      <div className="tracking-content">
        {activeTab === 'tracking' && renderTrackingContent()}
        {activeTab === 'history' && renderHistoryContent()}
        {activeTab === 'favorites' && renderFavoritesContent()}
      </div>

      {/* Notification Toggle */}
      <div className="notification-toggle">
        <label>
          <input
            type="checkbox"
            checked={enableNotifications}
            onChange={(e) => setEnableNotifications(e.target.checked)}
          />
          Enable notifications
        </label>
      </div>

      {/* Save to Favorites Modal */}
      {showFavoriteModal && (
        <div className="modal-overlay" onClick={() => setShowFavoriteModal(false)}>
          <div className="favorite-modal" onClick={e => e.stopPropagation()}>
            <h3>Save as Favorite</h3>
            <input
              type="text"
              placeholder="Name this order (e.g., 'My usual')"
              value={favoriteName}
              onChange={(e) => setFavoriteName(e.target.value)}
              autoFocus
            />
            <div className="modal-actions">
              <button onClick={() => setShowFavoriteModal(false)}>Cancel</button>
              <button className="save-btn" onClick={handleSaveToFavorites}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTracking;
